import {
  Component,
  OnInit,
  ChangeDetectorRef,
  OnDestroy,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule, NgForm } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';

import { ShiftsService } from './shifts.service';
import { Shift, ShiftsResponse } from '../models/shifts.model';
import { AppConfigTopNavComponent } from '../../../shared/app-config-nav/app-config-top-nav.component';
import { SearchBarComponent } from '../../../shared/search-bar/search-bar.component';

interface ShiftFiltersVm {
  searchTerm: string;
}

@Component({
  selector: 'app-shifts',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AppConfigTopNavComponent,
    RouterModule,
    SearchBarComponent,
  ],
  templateUrl: './shifts.component.html',
  styleUrls: ['./shifts.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ShiftsComponent implements OnInit, OnDestroy {
  shifts$: Observable<Shift[]> = of([]);
  isLoading = false;
  errorMessage: string | null = null;

  currentPage = 1;
  pageSize = 25;
  pageSizeOptions = [25, 50, 100, 500];
  totalPages = 1;

  filters: ShiftFiltersVm = {
    searchTerm: '',
  };

  modalOpen = false;
  isEditMode = false;
  currentShift: Shift | null = null;
  formModel: {
    name: string;
    gracePeriodMinutes: number | null;
  } = this.getEmptyShift();

  deleteConfirmOpen = false;
  shiftToDelete: Shift | null = null;

  toastVisible = false;
  toastMessage = '';
  toastType: 'success' | 'error' = 'success';

  constructor(
    private readonly shiftsService: ShiftsService,
    private readonly cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadShifts();
  }

  ngOnDestroy(): void {
    this.unlockBodyScroll();
  }

  private lockBodyScroll(): void {
    document.body.style.overflow = 'hidden';
  }

  private unlockBodyScroll(): void {
    document.body.style.overflow = '';
  }

  private syncBodyScrollLock(): void {
    if (this.modalOpen || this.deleteConfirmOpen) {
      this.lockBodyScroll();
      return;
    }

    this.unlockBodyScroll();
  }

  loadShifts(): void {
    this.isLoading = true;
    this.errorMessage = null;

    this.shifts$ = this.shiftsService
      .getShifts(this.currentPage, this.pageSize, {
        searchTerm: this.filters.searchTerm,
      })
      .pipe(
        map((res: ShiftsResponse | null) => {
          if (!res) {
            this.totalPages = 1;
            return [];
          }

          this.totalPages = res.totalPages ?? 1;
          return res.data ?? [];
        }),
        catchError(() => {
          this.errorMessage = 'Failed to load shifts';
          this.totalPages = 1;
          return of([]);
        }),
        finalize(() => {
          this.isLoading = false;
          this.cd.markForCheck();
        }),
      );
  }

  onSearchTermChange(value: string | null): void {
    this.filters.searchTerm = String(value ?? '').trim();
    this.currentPage = 1;
    this.loadShifts();
  }

  openCreateModal(): void {
    this.isEditMode = false;
    this.currentShift = null;
    this.formModel = this.getEmptyShift();
    this.modalOpen = true;

    this.syncBodyScrollLock();
  }

  openEditModal(shift: Shift): void {
    this.isEditMode = true;
    this.currentShift = { ...shift };

    this.formModel = {
      name: shift.name ?? '',
      gracePeriodMinutes:
        typeof shift.gracePeriodMinutes === 'number' ? shift.gracePeriodMinutes : 0,
    };

    this.modalOpen = true;

    this.syncBodyScrollLock();
  }

  closeModal(): void {
    this.modalOpen = false;
    this.isEditMode = false;
    this.currentShift = null;
    this.formModel = this.getEmptyShift();

    this.syncBodyScrollLock();
  }

  submitShift(form?: NgForm): void {
    const newName = this.formModel.name?.trim() ?? '';
    const newGracePeriodMinutes = Number(this.formModel.gracePeriodMinutes);

    if (!newName) {
      this.showToast('Name is required', 'error');
      return;
    }

    if (!Number.isFinite(newGracePeriodMinutes) || newGracePeriodMinutes < 0) {
      this.showToast('Grace period minutes must be 0 or greater', 'error');
      return;
    }

    if (this.isEditMode) {
      if (!this.currentShift?.id) {
        this.showToast('Unable to determine which shift to update', 'error');
        return;
      }

      const payload = {
        name: newName,
        GracePeriodMinutes: newGracePeriodMinutes,
      };

      this.shiftsService.updateShift(this.currentShift.id, payload).subscribe({
        next: (result) => {
          if (!result.success) {
            this.showToast(result.message, 'error');
            this.cd.detectChanges();
            return;
          }

          this.closeModal();
          this.showToast(result.message, 'success');
          this.loadShifts();
          this.cd.detectChanges();
        },
        error: (err) => {
          this.showToast(
            err?.error?.message || err?.message || 'Failed to update shift',
            'error',
          );
          this.cd.detectChanges();
        },
      });

      return;
    }

    const createPayload = {
      name: newName,
      GracePeriodMinutes: newGracePeriodMinutes,
    };

    this.shiftsService.createShift(createPayload).subscribe({
      next: (result) => {
        if (!result.success) {
          this.showToast(result.message, 'error');
          this.cd.detectChanges();
          return;
        }

        this.closeModal();
        this.showToast(result.message, 'success');
        this.loadShifts();
        this.cd.detectChanges();
      },
      error: (err) => {
        this.showToast(
          err?.error?.message || err?.message || 'Failed to create shift',
          'error',
        );
        this.cd.detectChanges();
      },
    });
  }

  confirmDelete(shift: Shift): void {
    this.shiftToDelete = shift;
    this.deleteConfirmOpen = true;

    this.syncBodyScrollLock();
  }

  cancelDelete(): void {
    this.deleteConfirmOpen = false;
    this.shiftToDelete = null;

    this.syncBodyScrollLock();
  }

  deleteShift(): void {
    if (!this.shiftToDelete) return;

    this.shiftsService.deleteShift(this.shiftToDelete.id).subscribe({
      next: (result) => {
        if (!result.success) {
          this.showToast(result.message, 'error');
          this.cd.detectChanges();
          return;
        }

        this.cancelDelete();
        this.showToast(result.message, 'success');
        this.loadShifts();
        this.cd.detectChanges();
      },
      error: (err) => {
        this.showToast(
          err?.error?.message || err?.message || 'Failed to delete shift',
          'error',
        );
        this.cd.detectChanges();
      },
    });
  }

  showToast(message: string, type: 'success' | 'error'): void {
    this.toastMessage = message;
    this.toastType = type;
    this.toastVisible = true;

    setTimeout(() => {
      this.toastVisible = false;
      this.cd.markForCheck();
    }, 2500);
  }

  getEmptyShift(): { name: string; gracePeriodMinutes: number | null } {
    return {
      name: '',
      gracePeriodMinutes: 0,
    };
  }

  getPayCodesLabel(shift: Shift | null | undefined): string {
    if (!shift?.payCodes?.length) return '—';
    return shift.payCodes.map((pc) => pc.name).join(', ');
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadShifts();
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadShifts();
    }
  }

  changePageSize(): void {
    this.currentPage = 1;
    this.loadShifts();
  }
}